import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import { Tabs } from "@covertree/ui/table/Tabs";
import { TabPanel } from "@covertree/ui/table/TabPanel";

import { PaymentSchedule } from "@covertree/agent-portal-graphql";

import { useAccessPattern } from "@hooks/useAccessPattern";
import { featureFlags } from "@utils/featureFlags";

import { Documents } from "./Documents";
import { Overview } from "./Overview";
import { PersonalDetails } from "./PersonalDetails";
import { Payment } from "./Payment";
import { PolicyRenewals } from "./PolicyRenewals";
import { EndorsementsList } from "./Endorsements";
import { PolicyDetailsContentProps } from "../types";
import { AdditionalInterests } from "./AdditionalInterests";

export function ContentTabs({
  policy,
  error,
  loading,
}: PolicyDetailsContentProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleChange = useCallback(
    (tabIndex: number) => {
      setActiveTab(tabIndex);
    },
    [setActiveTab]
  );

  const endorsementsTabEnabled =
    featureFlags.endorsementsTabEnabled.isEnabled();

  const { canSeeAdditionalInterestTab, canSeeEndorsementsTab } =
    useAccessPattern();

  const showEndorsementsTab = endorsementsTabEnabled && canSeeEndorsementsTab;

  const showPaymentTab = useMemo(
    () =>
      !!policy && policy.paymentScheduleName !== PaymentSchedule.MortgageBill,
    [policy]
  );

  const tabs = useMemo(
    () => [
      { label: "Overview", value: "overview" },
      { label: "Documents", value: "documents" },
      { label: "Personal details", value: "personalDetails" },
      ...(showPaymentTab ? [{ label: "Payment", value: "payment" }] : []),
      ...(canSeeAdditionalInterestTab
        ? [{ label: "Additional Interest", value: "additionalInterest" }]
        : []),
      ...(showEndorsementsTab
        ? [{ label: "Endorsements", value: "endorsements" }]
        : []),
      { label: "Renewals", value: "renewals" },
    ],
    [showPaymentTab, canSeeAdditionalInterestTab, showEndorsementsTab]
  );

  useEffect(() => {
    const requestedTab = searchParams.get("tab");

    if (!requestedTab || !policy) return;

    const index = tabs.findIndex((tab) => tab.value === requestedTab);

    if (index < 0) return;

    setActiveTab(index);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("tab");
    setSearchParams(nextParams, { replace: true });
  }, [policy, searchParams, tabs, setSearchParams]);

  const { renewalTabIndex, additionalInterestsTabIndex, endorsementsTabIndex } =
    useMemo(() => {
      const baseIndex = showPaymentTab ? 4 : 3;

      const additionalInterestsTabIndex = baseIndex;

      const endorsementsTabIndex = canSeeAdditionalInterestTab
        ? baseIndex + 1
        : baseIndex;

      const renewalTabIndex =
        baseIndex +
        (canSeeAdditionalInterestTab ? 1 : 0) +
        (showEndorsementsTab ? 1 : 0);

      return {
        additionalInterestsTabIndex,
        endorsementsTabIndex,
        renewalTabIndex,
      };
    }, [showPaymentTab, canSeeAdditionalInterestTab, showEndorsementsTab]);

  return (
    <Box>
      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={handleChange}
        disabled={(!policy && loading) || error}
        scrollButtons
        allowScrollButtonsMobile
        variant="scrollable"
      />
      <TabPanel value={activeTab} index={activeTab} py={3}>
        {activeTab === 0 && (
          <Overview policy={policy} loading={loading} error={error} />
        )}
        {activeTab === 1 && <Documents policy={policy} loading={loading} />}
        {activeTab === 2 && (
          <PersonalDetails policy={policy} loading={loading} error={error} />
        )}
        {showPaymentTab && activeTab === 3 && (
          <Payment policy={policy} loading={loading} error={error} />
        )}
        {canSeeAdditionalInterestTab &&
          activeTab === additionalInterestsTabIndex && (
            <AdditionalInterests
              policy={policy}
              loading={loading}
              error={error}
            />
          )}
        {showEndorsementsTab && activeTab === endorsementsTabIndex && (
          <EndorsementsList policy={policy} loading={loading} />
        )}
        {activeTab === renewalTabIndex && (
          <PolicyRenewals policy={policy} loading={loading} />
        )}
      </TabPanel>
    </Box>
  );
}
